'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, MapPin, Bookmark, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Props {
  userId: string;
}

interface SavedCafe {
  id: string;
  cafeId: string;
  name: string;
  area: string;
  imageUrl: string;
  rating: number;
  priceRange: string;
  isOpen: boolean;
}

export default function SavedPlacesGrid({ userId }: Props) {
  const supabase = createClient();
  const [saved, setSaved] = useState<SavedCafe[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSaved = async () => {
      const { data, error } = await supabase
        .from('saved_places')
        .select(`id, coffee_shop_id, coffee_shops(id, name, area, image_url, rating, price_range, is_open)`)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.log('Saved places error:', error.message);
        setLoading(false);
        return;
      }

      setSaved(
        (data || []).map((s: any) => ({
          id: s.id,
          cafeId: s.coffee_shop_id,
          name: s.coffee_shops?.name || 'Cafe',
          area: s.coffee_shops?.area || 'Malang',
          imageUrl: s.coffee_shops?.image_url || '',
          rating: s.coffee_shops?.rating || 0,
          priceRange: s.coffee_shops?.price_range || 'Rp 20K+',
          isOpen: s.coffee_shops?.is_open ?? true,
        }))
      );
      setLoading(false);
    };

    fetchSaved();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }

  if (saved.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-5xl mb-4">🔖</div>
        <p className="text-base font-700 text-primary mb-2">Belum ada tempat tersimpan</p>
        <p className="text-sm text-muted-foreground mb-5">Tap ikon bookmark di halaman cafe untuk menyimpannya ke sini.</p>
        <Link href="/" className="btn-primary inline-flex">Explore Cafe</Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {saved.map((cafe) => (
        <Link key={`saved-${cafe.id}`} href={`/coffeshop-detail-page?id=${cafe.cafeId}`} className="place-card">
          <div className="relative h-36 overflow-hidden">
            <AppImage
              src={cafe.imageUrl}
              alt={`Foto ${cafe.name}, cafe tersimpan`}
              fill
              className="object-cover"
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            />
            <div className="absolute top-2 right-2 w-7 h-7 bg-accent rounded-full flex items-center justify-center shadow-card">
              <Bookmark size={13} color="white" className="fill-current" />
            </div>
            <div className="absolute top-2 left-2">
              <span className={cafe.isOpen ? 'open-badge' : 'closed-badge'} style={{ fontSize: '9px', padding: '2px 6px' }}>
                {cafe.isOpen ? 'Buka' : 'Tutup'}
              </span>
            </div>
          </div>
          <div className="p-3">
            <h3 className="text-sm font-700 text-foreground line-clamp-1">{cafe.name}</h3>
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{cafe.area}</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1">
                <Star size={11} className="rating-star fill-current" />
                <span className="text-xs font-700 font-tabular">{cafe.rating?.toFixed(1)}</span>
              </div>
              <span className="text-xs text-muted-foreground">{cafe.priceRange?.split(' - ')[0] || 'Rp 20K+'}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}